import React, { useState, useEffect, useRef } from "react";
import styles from "./dropdown.module.css";
import { MyIcons } from "../../icons/my-icons";
import Checkbox from "../checkbox";

const Dropdown = ({
  label,
  options,
  onSelect,
  style,
  itemStyle,
  mainStyle,
  selected: initialSelected,
  multiSelect = false, // Nueva prop para activar multiselect
  clearable = true, // Nueva prop para controlar si se puede limpiar
  disabled = false, // Nueva prop para deshabilitar el dropdown
  placeholder = "Seleccionar",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(
    multiSelect ? initialSelected || [] : initialSelected || null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    setSelected(multiSelect ? initialSelected || [] : initialSelected);
  }, [initialSelected, multiSelect]);

  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  // Manejar clics fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Close menu but keep the typed searchTerm so it doesn't disappear on blur/tab
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options || []);
    } else {
      const filtered = (options || []).filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    // reset highlighted index when options change, but only if we're typing or the menu just opened
    // Don't reset if user is just hovering with mouse
    setHighlightedIndex((prev) => {
      const newLen = (searchTerm.trim() ? (options || []).filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase())).length : (options || []).length);
      // Only reset to 0 if the menu is opening or if we don't have a valid index
      if (prev < 0 || prev >= newLen) {
        return newLen > 0 ? 0 : -1;
      }
      // Keep current index if it's still valid
      return prev;
    });
  }, [searchTerm, options]);

  // When highlightedIndex changes, ensure the highlighted li is visible
  useEffect(() => {
    if (!isOpen) return;
    if (highlightedIndex == null || highlightedIndex < 0) return;
    const node = optionRefs.current[highlightedIndex];
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen, filteredOptions]);

  // keep optionRefs length in sync with filteredOptions to avoid stale refs
  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, (filteredOptions || []).length);
  }, [filteredOptions]);

  const handleArrowClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    // Do not clear searchTerm when closing via arrow click; preserve typed input.
    if (!newIsOpen) {
      setHighlightedIndex(0);
    }
  };

  const handleInputClick = () => {
    if (disabled) return;
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputFocus = () => {
    if (disabled) return;
    // Do not open the menu on focus (e.g., when tabbing). Keep existing typed text.
    setHighlightedIndex(0);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setIsTyping(true);
    // Open the menu when the user types something non-empty
    if (!isOpen && val.trim() !== "") {
      setIsOpen(true);
      setHighlightedIndex(0);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm("");
    setIsTyping(false);
    if (multiSelect) {
      setSelected([]);
      if (onSelect) onSelect([]);
    } else {
      setSelected(null);
      if (onSelect) onSelect(null);
    }
  };

  const handleSelect = (option) => {
    if (multiSelect) {
      // Lógica para multiselect
      const isSelected = selected.some((item) => item.value === option.value);
      let newSelected;

      if (isSelected) {
        // Remover si ya está seleccionado
        newSelected = selected.filter((item) => item.value !== option.value);
      } else {
        // Agregar si no está seleccionado
        newSelected = [...selected, option];
      }

      setSelected(newSelected);
      if (onSelect) onSelect(newSelected);
    } else {
      // Lógica para select simple
      setSelected(option);
      setSearchTerm("");
      setIsTyping(false);
      setIsOpen(false);
      if (onSelect) onSelect(option);
    }
  };

  // Función para obtener el texto del input
  const getInputValue = () => {
    // Si está escribiendo, siempre mostrar el searchTerm (incluso vacío)
    if (isTyping) return searchTerm;
    
    if (multiSelect) {
      if (selected.length === 0) return "";
      if (selected.length === 1) return selected[0].label;
      return `${selected.length} elementos seleccionados`;
    } else {
      return selected ? selected.label : "";
    }
  };

  // Función para verificar si una opción está seleccionada
  const isOptionSelected = (option) => {
    if (multiSelect) {
      return selected.some((item) => item.value === option.value);
    } else {
      return selected?.value === option.value;
    }
  };

  return (
    <div className={styles.dropdown} style={mainStyle} ref={dropdownRef}>
      {label && <label className={styles.label}>{label}</label>}

      <div
        className={`${styles.toggle} ${
          (multiSelect ? selected.length > 0 : selected)
            ? styles.selected
            : styles.unselected
        } ${disabled ? styles.disabled : ''}`}
        style={{
          ...style,
          opacity: disabled ? 0.6 : 1
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className={styles.inputField}
          placeholder={placeholder}
          value={getInputValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onKeyDown={(e) => {
            // Close on Escape
            if (e.key === 'Escape' || e.key === 'Esc') {
              e.preventDefault();
              // Close but keep searchTerm so typed content doesn't disappear
              setIsOpen(false);
              setHighlightedIndex(0);
              if (inputRef.current) inputRef.current.blur();
              return;
            }

            // If menu is closed, Arrow keys open it and set an initial highlight
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isOpen) {
              e.preventDefault();
              setIsOpen(true);
              setIsTyping(true);
              setHighlightedIndex(e.key === 'ArrowDown' ? 0 : ((filteredOptions.length ? filteredOptions.length - 1 : 0)));
              return;
            }

            if (!isOpen) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlightedIndex((prev) => (prev + 1 < filteredOptions.length ? prev + 1 : 0));
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlightedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : filteredOptions.length - 1));
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              const opt = filteredOptions[highlightedIndex];
              if (opt) handleSelect(opt);
            }
          }}
          disabled={disabled}
          autoComplete="off"
        />
        <div className={styles.iconsContainer}>
          {(multiSelect ? selected.length > 0 : selected) && clearable && !disabled && (
            <div onClick={handleClear} className={styles.iconButton}>
              <MyIcons
                name="close"
                className={styles.icon}
                color="var(--dark-grey)"
                height="14px"
                width="14px"
              />
            </div>
          )}
          <div onClick={handleArrowClick} className={styles.iconButton}>
            <MyIcons
              name={isOpen ? "arrowUp" : "arrowDown"}
              className={styles.icon}
              color="var(--black)"
              height="24px"
              width="24px"
            />
          </div>
        </div>
      </div>
      {isOpen && (
        <div className={styles.menuContainer}>
          <ul className={styles.menu}>
            {filteredOptions.map((option, i) => (
              <li
                key={option.value}
                ref={(el) => (optionRefs.current[i] = el)}
                onMouseEnter={() => setHighlightedIndex(i)}
                onClick={() => handleSelect(option)}
                className={`${styles.item} ${option.isInHistory ? styles.history : ''} ${highlightedIndex === i ? styles.highlighted : ''}`}
                style={{...itemStyle, ...option.style}} // Combinar estilos globales con estilos individuales
              >
                {multiSelect && (
                  <Checkbox
                    checked={isOptionSelected(option)}
                    onChange={() => handleSelect(option)}
                    style={{ marginRight: 8, pointerEvents: "none" }} 
                    tabIndex={-1}
                  />
                )}
                {option.label}
              </li>
            ))}
            {filteredOptions.length === 0 && searchTerm.trim() && (
              <li className={styles.noResults}>
                No se encontraron resultados
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
